'use client';

import { type ReactNode, useLayoutEffect, useRef } from 'react';

import type { RVLayout } from './layout-managers/LayoutManager';

export interface ViewHolderProps {
  index: number;
  layout: RVLayout | null;
  inverted?: boolean;
  /**
   * When set, the cell receives `scroll-snap-align` so the parent
   * scroller's `scroll-snap-type` engages on this cell as a snap target.
   * Set by the engine when `pagingEnabled` is on.
   */
  snapAlignment?: 'start' | 'center' | 'end';
  onMeasure: (
    index: number,
    dimensions: { width: number; height: number },
  ) => void;
  children: ReactNode;
}

export function ViewHolder({
  index,
  layout,
  inverted,
  snapAlignment,
  onMeasure,
  children,
}: ViewHolderProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const indexRef = useRef(index);
  indexRef.current = index;
  const onMeasureRef = useRef(onMeasure);
  onMeasureRef.current = onMeasure;
  // Tracks whether this view has ever had a non-null layout. Before the
  // recycler hands us a layout, the cell renders at its content's natural
  // width — feeding that into the rolling average corrupts size estimates
  // for items further down the list. This ref is updated during render so
  // the ResizeObserver callback (which is closed over the first render's
  // scope) reads the latest value.
  const hasLayoutRef = useRef(false);
  if (layout != null) hasLayoutRef.current = true;

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const report = (width: number, height: number) => {
      // Gate on having a real layout (i.e. the recycler is actively
      // positioning this cell). Pre-layout measurements reflect the cell's
      // intrinsic content size, not the size the layout manager assigned.
      if (!hasLayoutRef.current) return;
      // jsdom (and elements before they have content) report zero
      // dimensions; treat those as "no measurement yet".
      if (width === 0 && height === 0) return;
      onMeasureRef.current(indexRef.current, { width, height });
    };

    const rect = element.getBoundingClientRect();
    report(rect.width, rect.height);

    const ObserverCtor = (
      globalThis as { ResizeObserver?: typeof ResizeObserver }
    ).ResizeObserver;
    if (!ObserverCtor) return;

    const observer = new ObserverCtor(entries => {
      const entry = entries[0];
      if (!entry) return;
      report(entry.contentRect.width, entry.contentRect.height);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const style = layout
    ? {
        position: 'absolute' as const,
        left: 0,
        top: 0,
        width: layout.width,
        // translate3d carries BOTH layout.x and layout.y. For linear lists
        // x is always 0, but grid items in columns 2..N rely on the x
        // offset to land in the right column. Inverted: translate3d FIRST
        // in the list so it runs LAST in the matrix product — scaleY(-1)
        // un-flips the cell's content, then translate3d positions it. The
        // opposite order multiplies the y by -1.
        transform: inverted
          ? `translate3d(${layout.x}px, ${layout.y}px, 0) scaleY(-1)`
          : `translate3d(${layout.x}px, ${layout.y}px, 0)`,
        ...(snapAlignment ? { scrollSnapAlign: snapAlignment } : {}),
      }
    : snapAlignment
      ? { scrollSnapAlign: snapAlignment }
      : undefined;

  return (
    <div ref={elementRef} style={style} data-flash-list-cell-index={index}>
      {children}
    </div>
  );
}
